import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCategoryStore } from '../stores/category-store';
import { useFileStore } from '../stores/file-store';
import { 
  Folder, MoreVertical, Upload, Grid, List, Trash2, 
  FolderOpen, ArrowRight, X, 
  Eye, EyeOff, Shield, Star, Plus, Download, Archive, FileDown,
  Globe, MapPin, Loader2, Copy, Link2, FileImage
} from 'lucide-react';
import { VisibilityType } from '../types';
import MoreMenu from '../components/shared/MoreMenu';
import { cn } from '../utils/cn';
import { formatFileSize, getFileIconByExtension, getFileIconColor } from '../utils/file-types';
import FileCard from '../components/files/FileCard';
import UploadModal from '../components/files/UploadModal';
import CreateCategoryModal from '../components/categories/CreateCategoryModal';
import CategorySelector from '../components/categories/CategorySelector';
import OrderBindingInfo from '../components/categories/OrderBindingInfoNew';
import DuplicateFilesModal from '../components/files/DuplicateFilesModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useCategoryStore as useCatStore } from '../stores/category-store';
import { useI18n } from '../i18n';

const CategoriesPageNew: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { categories, setActiveCategory, fetchCategories, updateCategory } = useCategoryStore();
  const { 
    deleteFiles,
    fetchCategoryFiles,
    categoryFiles,
    categoryFilesLoading,
    categoryFilesHasMore,
    categoryFilesTotalCount,
    categoryFilesTotalSize,
    generateThumbnailsFor
  } = useFileStore();
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());
  
  // 无限滚动 ref
  const fileListRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ processed: number; total: number } | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);
  const [moveProgress, setMoveProgress] = useState<{ processed: number; total: number } | null>(null);
  const [moveStep, setMoveStep] = useState<'select' | 'confirm'>('select');
  const [showMoveCategoryModal, setShowMoveCategoryModal] = useState(false);
  const [moveCategoryTarget, setMoveCategoryTarget] = useState<string | null>(null);
  const [isCategoryMoving, setIsCategoryMoving] = useState(false);
  const [moveCategoryStep, setMoveCategoryStep] = useState<'select' | 'confirm'>('select');
  const moveCategory = useCatStore().moveCategory;
  const { t } = useI18n();
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearStep, setClearStep] = useState<'confirm-files' | 'deleting-files' | 'confirm-category'>('confirm-files');
  const [clearProgress, setClearProgress] = useState<{ processed: number; total: number } | null>(null);
  const [clearTreeInfo, setClearTreeInfo] = useState<{ fileIds: string[]; categoryIds: string[]; fileNames: string[] } | null>(null);
  const [clearTargetCategory, setClearTargetCategory] = useState<any | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number; fileName?: string } | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [selectedVisibilityType, setSelectedVisibilityType] = useState<VisibilityType>('private');
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<'cn' | 'intl'>('cn');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showExternalLinkModal, setShowExternalLinkModal] = useState(false);
  const [externalLinkTargets, setExternalLinkTargets] = useState<{ id: string; name: string; depth: number }[]>([]);
  const [isUpdatingExternalLink, setIsUpdatingExternalLink] = useState(false);
  const [thumbnailGenProgress, setThumbnailGenProgress] = useState<{ processed: number; total: number; phase: string } | null>(null);
  const [pageConfirm, setPageConfirm] = useState<{
    open: boolean; title: string; message: string;
    variant: 'danger' | 'warning' | 'default'; confirmText: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', confirmText: '确定', onConfirm: () => {} });

  // 获取可见性类型的显示信息
  const getVisibilityInfo = (visibilityType: VisibilityType | undefined) => {
    switch (visibilityType) {
      case 'public':
        return { label: '完全公开', icon: Eye, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' };
      case 'restricted_public':
        return { label: '受限公开', icon: Shield, color: 'text-orange-600', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' };
      default:
        return { label: '私有', icon: EyeOff, color: 'text-slate-500', bgColor: 'bg-slate-50', borderColor: 'border-slate-200' };
    }
  };

  const getCurrentVisibilityType = (category: any): VisibilityType => {
    if (category.visibility_type) return category.visibility_type;
    return category.is_public ? 'public' : 'private';
  };

  const siblingNamesForRename = React.useMemo(() => {
    const flat: any[] = [];
    const dfs = (arr: any[]) => { for (const c of arr) { flat.push(c); if (c.children?.length) dfs(c.children); } };
    dfs(categories as any[]);
    const current = flat.find(c => c.id === id);
    const parentId = current?.parent_id ?? null;
    return flat.filter(c => c.parent_id === parentId).map(c => (c.name || '').toLowerCase().trim());
  }, [categories, id]);

  const [isRenaming, setIsRenaming] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const [perFolderMenuOpen, setPerFolderMenuOpen] = useState<string>('');
  const [perFileMenuOpen, setPerFileMenuOpen] = useState<string>('');
  // 存储菜单触发按钮的位置信息
  const [perFileMenuRect, setPerFileMenuRect] = useState<DOMRect | null>(null);

  // 注意：不再使用全局点击监听来关闭菜单
  // MoreMenu 组件自己会处理点击外部关闭的逻辑
  // 这里只处理文件夹菜单
  useEffect(() => {
    const onDocClick = () => { 
      setPerFolderMenuOpen(''); 
      // 注意：不在这里关闭 perFileMenuOpen，让 MoreMenu 自己处理
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const flatCategories = React.useMemo(() => {
    const flat: any[] = [];
    const dfs = (arr: any[]) => { for (const c of arr) { flat.push(c); if (c.children?.length) dfs(c.children); } };
    dfs(categories as any[]);
    return flat;
  }, [categories]);

  const getPath = React.useCallback((cid?: string | null) => {
    if (!cid) return t('breadcrumbCategories');
    const chain: string[] = [];
    let cur: any | undefined = flatCategories.find((c: any) => c.id === cid);
    while (cur) {
      chain.unshift(cur.name);
      if (!cur.parent_id) break;
      cur = flatCategories.find((c: any) => c.id === cur.parent_id);
    }
    return chain.length ? chain.join(' / ') : t('breadcrumbCategories');
  }, [flatCategories, t]);

  const collectTreeInfo = React.useCallback((root: any | null) => {
    const fileSet = new Set<string>();
    const fileNames: string[] = [];
    const catList: string[] = [];
    const dfs = (node: any) => {
      if (!node) return;
      catList.push(node.id);
      (node.files || []).forEach((f: any) => { fileSet.add(f.id); fileNames.push(f.name); });
      (node.children || []).forEach((c: any) => dfs(c));
    };
    dfs(root);
    return { fileIds: Array.from(fileSet), categoryIds: catList, fileNames };
  }, []);

  const collectDescendantIds = React.useCallback((node: any): Set<string> => {
    const set = new Set<string>();
    const rec = (n: any) => { (n.children || []).forEach((c: any) => { set.add(c.id); rec(c); }); };
    rec(node);
    return set;
  }, []);

  // Find the active category from the ID in URL
  const findCategoryById = React.useCallback((cats: any[], targetId: string): any | null => {
    for (const cat of cats) {
      if (cat.id === targetId) return cat;
      if (cat.children?.length) {
        const found = findCategoryById(cat.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);
  
  // 使用 useMemo 稳定 activeCategory 引用，避免每次渲染创建新对象
  const activeCategory = React.useMemo(() => {
    if (!id) return null;
    return findCategoryById(categories, id);
  }, [id, categories, findCategoryById]);
  
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  
  // 只在 id 或 categories 变化时更新 store 中的 activeCategory
  useEffect(() => { 
    setActiveCategory(activeCategory); 
  }, [id, categories.length, setActiveCategory, activeCategory]); // 使用 id 和 categories.length 来检测变化

  const currentChildren = React.useMemo(() => (
    activeCategory ? (activeCategory.children || []) : (categories || [])
  ), [activeCategory, categories]);
  
  // 🆕 Apple 模式：使用缓存的文件数据
  const currentFiles = React.useMemo(() => {
    if (!id) return [];
    return categoryFiles.get(id) || [];
  }, [id, categoryFiles]);
  
  // 🆕 当前分类的加载状态（严格按ID检查）
  const isLoadingFiles = id ? (categoryFilesLoading.get(id) === true) : false;
  const hasMoreFiles = id ? (categoryFilesHasMore.get(id) === true) : false;
  const totalFilesCount = id ? (categoryFilesTotalCount.get(id) ?? (activeCategory?.file_count || 0)) : 0;
  const totalFilesSize = id ? (categoryFilesTotalSize.get(id) ?? 0) : 0;
  
  // 判断是否需要首次加载：
  // - hasMore 为 undefined 表示从未加载过这个分类
  // - hasMore 为 false 表示已经加载完成（包括空文件夹）
  // - hasMore 为 true 表示还有更多文件可加载
  const hasNeverLoaded = id && categoryFilesHasMore.get(id) === undefined;
  const needsInitialLoad = hasNeverLoaded && !isLoadingFiles;

  // 🆕 进入分类时按需加载（仅首次）
  useEffect(() => {
    if (needsInitialLoad && activeCategory && id) {
      console.log(`[CategoriesPageNew] 首次加载分类: ${id}`);
      fetchCategoryFiles(id);
    }
  }, [needsInitialLoad, activeCategory, id, fetchCategoryFiles]);
  
  // 🆕 加载更多文件的回调（用于大文件夹分页）
  const loadMoreFiles = useCallback(() => {
    // 只有当：1. 有ID 2. 有更多文件 3. 不在加载中 4. 已有文件（不是首次加载）
    if (id && hasMoreFiles && !isLoadingFiles && currentFiles.length > 0) {
      console.log(`[CategoriesPageNew] 加载更多: ${currentFiles.length}/${totalFilesCount}`);
      fetchCategoryFiles(id);
    }
  }, [id, hasMoreFiles, isLoadingFiles, currentFiles.length, totalFilesCount, fetchCategoryFiles]);
  
  // 🆕 无限滚动 - 使用 IntersectionObserver（仅对大文件夹有效）
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    // 只有当有更多文件且已有内容时才启用观察器
    if (!trigger || !hasMoreFiles || currentFiles.length === 0) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreFiles && !isLoadingFiles) {
          loadMoreFiles();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );
    
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMoreFiles, isLoadingFiles, loadMoreFiles, currentFiles.length]);

  // 如果URL有id但找不到分类
  if (!activeCategory && id) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Folder size={48} className="mb-4 text-slate-300" />
        <p className="text-lg font-medium text-slate-500">{t('categoryNotFound')}</p>
        <button
          onClick={() => navigate('/uadmin/files')}
          className="mt-4 px-4 py-2 text-sm font-medium text-sky-600 bg-sky-50 rounded-lg hover:bg-sky-100 transition-colors"
        >
          {t('filesTitle')}
        </button>
      </div>
    );
  }

  const visibilityInfo = activeCategory ? getVisibilityInfo(getCurrentVisibilityType(activeCategory)) : null;
  const VisibilityIcon = visibilityInfo?.icon;

  return (
    <div className="space-y-6">
      {/* 页面头部 - 文件夹信息卡片 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* 左侧：文件夹信息 */}
            <div className="flex items-start gap-4">
              <div className="p-3 bg-sky-50 rounded-2xl text-sky-500 border border-sky-100">
                <Folder size={32} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-800">
                    {activeCategory ? activeCategory.name : t('categoriesAll')}
                  </h1>
                  {/* 可见性徽章 */}
                  {visibilityInfo && VisibilityIcon && (
                    <button
                      onClick={async () => {
                        try {
                          const { data: { user } } = await (await import('../config/supabase')).supabase.auth.getUser();
                          if (!user) return;
                          const { data: profile } = await (await import('../config/supabase')).supabase.from('v_fs_users').select('role').eq('id', user.id).single();
                          if (profile?.role !== 'admin' && profile?.role !== 'super_admin') return alert('仅管理员可修改可见性');
                          setSelectedVisibilityType(getCurrentVisibilityType(activeCategory));
                          setShowVisibilityModal(true);
                        } catch (e: any) { alert(e?.message || '获取权限失败'); }
                      }}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors hover:opacity-80',
                        visibilityInfo.bgColor, visibilityInfo.color, visibilityInfo.borderColor
                      )}
                    >
                      <VisibilityIcon size={12} />
                      {visibilityInfo.label}
                    </button>
                  )}
                  {/* 租户徽章 - 可点击编辑 */}
                  {activeCategory && (
                    <button
                      onClick={async () => {
                        try {
                          const { data: { user } } = await (await import('../config/supabase')).supabase.auth.getUser();
                          if (!user) return;
                          const { data: profile } = await (await import('../config/supabase')).supabase.from('v_fs_users').select('role, can_access_all_tenants').eq('id', user.id).single();
                          // 只有超级管理员可以修改租户
                          if (profile?.role !== 'super_admin' && !(profile?.role === 'admin' && profile?.can_access_all_tenants)) {
                            return alert('仅超级管理员可修改文件夹区域');
                          }
                          setSelectedTenant(activeCategory.tenant || 'cn');
                          setShowTenantModal(true);
                        } catch (e: any) { alert(e?.message || '获取权限失败'); }
                      }}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors hover:opacity-80 cursor-pointer',
                        activeCategory.tenant === 'intl'
                          ? 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
                          : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                      )}
                    >
                      {activeCategory.tenant === 'intl' ? (
                        <><Globe size={12} /> 国际</>
                      ) : (
                        <><MapPin size={12} /> 国内</>
                      )}
                    </button>
                  )}
                  {/* 对外关联徽章 - 点击弹出确认弹窗 */}
                  {activeCategory && (
                    <button
                      onClick={async () => {
                        try {
                          const { data: { user } } = await (await import('../config/supabase')).supabase.auth.getUser();
                          if (!user) return;
                          const { data: profile } = await (await import('../config/supabase')).supabase.from('v_fs_users').select('role').eq('id', user.id).single();
                          if (profile?.role !== 'admin' && profile?.role !== 'super_admin') return alert('仅管理员可修改对外关联设置');
                          // 递归收集当前分类 + 所有子文件夹
                          const targets: { id: string; name: string; depth: number }[] = [];
                          const collectChildren = (cat: any, depth: number) => {
                            targets.push({ id: cat.id, name: cat.name, depth });
                            if (cat.children?.length) {
                              for (const child of cat.children) collectChildren(child, depth + 1);
                            }
                          };
                          collectChildren(activeCategory, 0);
                          setExternalLinkTargets(targets);
                          setShowExternalLinkModal(true);
                        } catch (e: any) { alert(e?.message || '操作失败'); }
                      }}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors hover:opacity-80 cursor-pointer',
                        activeCategory.is_external_linked
                          ? 'bg-teal-50 text-teal-600 border-teal-200 hover:bg-teal-100'
                          : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'
                      )}
                      title={activeCategory.is_external_linked ? '已对外关联（会员中心可见）' : '未对外关联 点击设置'}
                    >
                      <Link2 size={12} />
                      {activeCategory.is_external_linked ? '已关联' : '未关联'}
                    </button>
                  )}
                </div>
                
                {/* 统计信息 */}
                <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                  <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium">
                    {(currentChildren?.length || 0) + totalFilesCount} 项目
                  </span>
                  {(activeCategory?.file_count || 0) > 0 && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                      <span>{activeCategory.file_count} 文件</span>
                    </>
                  )}
                  {totalFilesSize > 0 && (
                    <>
                      <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                      <span className="text-sky-600 font-medium">{formatFileSize(totalFilesSize)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 右侧：操作按钮 */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg shadow-sm hover:shadow transition-all font-medium text-sm"
              >
                <Upload size={18} />
                上传
              </button>
              
              <div className="h-8 w-px bg-slate-200 mx-1 hidden sm:block"></div>
              
              {/* 视图切换 */}
              <div className="flex items-center bg-slate-100 border border-slate-200 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    'p-1.5 rounded transition-colors',
                    viewMode === 'grid' ? 'bg-white shadow-sm text-sky-600' : 'text-slate-500 hover:text-slate-700'
                  )}
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    'p-1.5 rounded transition-colors',
                    viewMode === 'list' ? 'bg-white shadow-sm text-sky-600' : 'text-slate-500 hover:text-slate-700'
                  )}
                >
                  <List size={18} />
                </button>
              </div>

              {activeCategory && (
                <>
                  <button
                    onClick={() => { setNewCategoryName(activeCategory.name); setShowRenameModal(true); }}
                    className="hidden md:inline-flex px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    重命名
                  </button>
                  <button
                    onClick={() => { const next = !selectMode; setSelectMode(next); if (!next) { setSelectedFileIds(new Set()); setSelectedFolderIds(new Set()); } }}
                    className={cn(
                      "px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                      selectMode 
                        ? "bg-sky-500 text-white hover:bg-sky-600" 
                        : "text-slate-600 border border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    {selectMode ? '完成' : '选择'}
                  </button>
                </>
              )}

              {/* 更多菜单 */}
              <div className="relative">
                <button
                  onClick={() => setTopMenuOpen(v => !v)}
                  className="p-2 text-slate-500 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <MoreVertical size={20} />
                </button>
                {topMenuOpen && activeCategory && (
                  <div className="absolute right-0 mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1">
                    <button onClick={() => { setTopMenuOpen(false); setShowMoveCategoryModal(true); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">{t('moveCurrentCategory')}</button>
                    <button onClick={() => { setTopMenuOpen(false); setShowDuplicateModal(true); }} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                      <Copy size={16} className="text-amber-500" />
                      检查重复文件
                    </button>
                    <button
                      onClick={async () => {
                        setTopMenuOpen(false);
                        if (!activeCategory) return;
                        // 找出所有 PDF 且没有缩略图的文件
                        const currentFiles = categoryFiles.get(activeCategory.id) || [];
                        const pdfFiles = currentFiles.filter(f => f.type === 'pdf' && !f.thumbnail_key);
                        if (pdfFiles.length === 0) {
                          const allPdfs = currentFiles.filter(f => f.type === 'pdf');
                          if (allPdfs.length === 0) {
                            setPageConfirm({ open: true, title: '提示', message: '当前文件夹没有 PDF 文件', variant: 'default', confirmText: '知道了', onConfirm: () => setPageConfirm(p => ({ ...p, open: false })) });
                            return;
                          }
                          setPageConfirm({
                            open: true, title: '重新生成缩略图', variant: 'warning', confirmText: '重新生成',
                            message: `当前文件夹有 ${allPdfs.length} 个 PDF 文件已有缩略图。\n是否重新生成为首页截图？`,
                            onConfirm: async () => {
                              setPageConfirm(p => ({ ...p, open: false }));
                              setThumbnailGenProgress({ processed: 0, total: allPdfs.length, phase: '生成 PDF 缩略图...' });
                              try {
                                await generateThumbnailsFor(
                                  allPdfs.map(f => f.id),
                                  (p, t) => setThumbnailGenProgress({ processed: p, total: t, phase: '生成 PDF 缩略图...' })
                                );
                                await fetchCategoryFiles(activeCategory.id, { reset: true });
                              } catch (e: any) { console.error('生成失败:', e); }
                              setThumbnailGenProgress(null);
                            },
                          });
                          return;
                        }
                        setPageConfirm({
                          open: true, title: '生成 PDF 缩略图', variant: 'default', confirmText: '开始生成',
                          message: `将为 ${pdfFiles.length} 个 PDF 生成首页截图缩略图，继续？`,
                          onConfirm: async () => {
                            setPageConfirm(p => ({ ...p, open: false }));
                            setThumbnailGenProgress({ processed: 0, total: pdfFiles.length, phase: '生成 PDF 缩略图...' });
                            try {
                              await generateThumbnailsFor(
                                pdfFiles.map(f => f.id),
                                (p, t) => setThumbnailGenProgress({ processed: p, total: t, phase: '生成 PDF 缩略图...' })
                              );
                              await fetchCategoryFiles(activeCategory.id, { reset: true });
                            } catch (e: any) { console.error('生成失败:', e); }
                            setThumbnailGenProgress(null);
                          },
                        });
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <FileImage size={16} className="text-teal-500" />
                      生成 PDF 缩略图
                    </button>
                    <div className="border-t border-slate-100 my-1"></div>
                    <button onClick={() => { setTopMenuOpen(false); setClearTargetCategory(activeCategory); setClearTreeInfo(collectTreeInfo(activeCategory)); setClearStep('confirm-files'); setShowClearModal(true); }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">{t('clearAndDeleteCategory')}</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 订单绑定信息 - 重新设计 */}
          {activeCategory && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <OrderBindingInfo 
                categoryId={activeCategory.id} 
                onUpdate={() => fetchCategories()}
              />
            </div>
          )}
        </div>
      </div>

      {/* 批量操作底部工具栏 - Apple 风格 */}
      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              {/* 左侧：选择信息和快捷操作 */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">
                  已选择 <span className="text-sky-600 font-semibold">{selectedFileIds.size + selectedFolderIds.size}</span> 项
                </span>
                <div className="h-4 w-px bg-slate-300 hidden sm:block"></div>
                <div className="hidden sm:flex items-center gap-2">
                  <button 
                    onClick={() => {
                      setSelectedFileIds(new Set((currentFiles || []).map((f: any) => f.id)));
                      setSelectedFolderIds(new Set((currentChildren || []).map((c: any) => c.id)));
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-sky-600 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors"
                  >
                    全选
                  </button>
                  <button 
                    onClick={() => { setSelectedFileIds(new Set()); setSelectedFolderIds(new Set()); }}
                    className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    取消全选
                  </button>
                </div>
              </div>
              
              {/* 右侧：操作按钮 */}
              <div className="flex items-center gap-2">
                {/* 下载按钮 - 仅文件可下载 */}
                {selectedFileIds.size > 0 && selectedFolderIds.size === 0 && (
                  <button 
                    onClick={() => setShowDownloadModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    <Download size={16} />
                    <span className="hidden sm:inline">下载</span>
                  </button>
                )}
                
                {/* 移动按钮 */}
                <button 
                  onClick={() => { 
                    if ((selectedFileIds.size + selectedFolderIds.size) > 0) { 
                      setMoveTarget(activeCategory?.id || ''); 
                      setMoveStep('select'); 
                      setShowMoveModal(true); 
                    } 
                  }}
                  disabled={selectedFileIds.size + selectedFolderIds.size === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FolderOpen size={16} />
                  <span className="hidden sm:inline">移动</span>
                </button>
                
                {/* 删除按钮 */}
                <button 
                  onClick={() => { if (selectedFileIds.size > 0 || selectedFolderIds.size > 0) setShowDeleteModal(true); }}
                  disabled={selectedFileIds.size + selectedFolderIds.size === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">删除</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 固定文件夹区域 - 仿Apple设计 */}
      {currentChildren.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Star size={14} /> 子文件夹
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {currentChildren.map((subCategory: any) => (
              <div
                key={subCategory.id}
                className={cn(
                  "bg-white p-4 rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all cursor-pointer group relative",
                  selectedFolderIds.has(subCategory.id) && "ring-2 ring-sky-500 border-sky-400"
                )}
                onClick={() => {
                  if (selectMode) {
                    setSelectedFolderIds(prev => {
                      const next = new Set(prev);
                      next.has(subCategory.id) ? next.delete(subCategory.id) : next.add(subCategory.id);
                      return next;
                    });
                  } else {
                    navigate(`/uadmin/categories/${subCategory.id}`);
                  }
                }}
              >
                {selectMode && (
                  <div className="absolute top-3 left-3 z-10">
                    <input type="checkbox" className="h-4 w-4 accent-sky-600 cursor-pointer" checked={selectedFolderIds.has(subCategory.id)} onChange={() => {}} onClick={(e) => e.stopPropagation()} />
                  </div>
                )}
                <div className="flex items-start justify-between mb-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    subCategory.is_public ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"
                  )}>
                    <Folder size={24} />
                  </div>
                  {!selectMode && (
                    <div className="relative">
                      <button
                        className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                        onClick={(e) => { e.stopPropagation(); setPerFolderMenuOpen(subCategory.id === perFolderMenuOpen ? '' : subCategory.id); }}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {perFolderMenuOpen === subCategory.id && (
                        <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1">
                          <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={(e) => { e.stopPropagation(); setPerFolderMenuOpen(''); setMoveCategoryTarget(subCategory.id); setShowMoveCategoryModal(true); }}>移动此文件夹</button>
                          <button className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setPerFolderMenuOpen(''); setClearTargetCategory(subCategory); setClearTreeInfo(collectTreeInfo(subCategory)); setClearStep('confirm-files'); setShowClearModal(true); }}>删除此文件夹</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="font-medium text-slate-800 truncate">{subCategory.name}</div>
                <div className="text-xs text-slate-400 mt-1">{subCategory.file_count || 0} 个文件</div>
              </div>
            ))}
            
            {/* 新建文件夹入口 */}
            {activeCategory && (
              <button
                onClick={() => setShowCreateCategoryModal(true)}
                className="border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center p-4 hover:border-sky-300 hover:bg-sky-50/50 transition-all cursor-pointer text-slate-400 hover:text-sky-500 min-h-[120px]"
              >
                <div className="flex flex-col items-center">
                  <Plus size={24} className="mb-1" />
                  <span className="text-xs font-medium">新建子文件夹</span>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* 文件列表区域 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-4">
            <h3 className="text-base font-semibold text-slate-800">项目</h3>
            <span className="text-xs text-slate-400">
              {totalFilesCount > 0 
                ? (currentFiles.length >= totalFilesCount 
                    ? `${totalFilesCount} 文件` 
                    : `${currentFiles.length} / ${totalFilesCount} 文件`)
                : `${currentFiles.length} 文件`
              }
              {isLoadingFiles && <Loader2 className="inline-block ml-1 w-3 h-3 animate-spin" />}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {activeCategory && (
              <button onClick={() => setShowCreateCategoryModal(true)} className="text-sm px-3 py-1.5 text-slate-600 border border-slate-200 rounded-lg hover:bg-white transition-colors">
                新建子文件夹
              </button>
            )}
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div ref={fileListRef} className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currentFiles.map((file: any) => (
              <FileCard
                key={file.id}
                file={file}
                selectable={selectMode}
                selected={selectedFileIds.has(file.id)}
                onSelectChange={(id, checked) => {
                  setSelectedFileIds(prev => {
                    const next = new Set(prev);
                    checked ? next.add(id) : next.delete(id);
                    return next;
                  });
                }}
                onRequestMove={(id) => { setSelectedFileIds(new Set([id])); setMoveTarget(activeCategory?.id || ''); setMoveStep('select'); setShowMoveModal(true); }}
                onRequestDelete={(id) => { setSelectedFileIds(new Set([id])); setShowDeleteModal(true); }}
              />
            ))}
            
            {/* 空状态 */}
            {currentFiles.length === 0 && !isLoadingFiles && (
              <div className="col-span-full py-12 text-center text-slate-400">
                <Folder size={48} className="mx-auto mb-3 opacity-50" />
                <p>此文件夹暂无文件</p>
                <button onClick={() => setShowUploadModal(true)} className="mt-3 text-sm text-sky-500 hover:text-sky-600">
                  上传文件
                </button>
              </div>
            )}
            
            {/* 首次加载状态 */}
            {isLoadingFiles && currentFiles.length === 0 && (
              <div className="col-span-full py-12 flex flex-col items-center justify-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <span className="text-sm">加载文件中...</span>
              </div>
            )}
            
            {/* 加载更多触发器（仅对大文件夹有效） */}
            {hasMoreFiles && currentFiles.length > 0 && (
              <div 
                ref={loadMoreTriggerRef}
                className="col-span-full py-6 flex items-center justify-center"
              >
                {isLoadingFiles ? (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">加载更多...</span>
                  </div>
                ) : (
                  <button 
                    onClick={loadMoreFiles}
                    className="text-sm text-sky-500 hover:text-sky-600 px-4 py-2 rounded-lg hover:bg-sky-50 transition-colors"
                  >
                    加载更多 ({currentFiles.length} / {totalFilesCount})
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col">
            {/* 固定列表表头 */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wide sticky top-0 z-10 border-b border-slate-100">
              {selectMode && <div className="col-span-1"></div>}
              <div className={cn("col-span-6", selectMode && "col-span-5")}>名称</div>
              <div className="col-span-2">类型</div>
              <div className="col-span-2">大小</div>
              <div className="col-span-1"></div>
            </div>
            
            {/* 可滚动文件列表 - 最大高度 60vh */}
            <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
            
            {currentFiles.map((file: any) => (
              <div
                key={file.id}
                className={cn(
                  "grid grid-cols-12 gap-4 px-6 py-4 hover:bg-sky-50/50 transition-colors cursor-pointer items-center",
                  selectedFileIds.has(file.id) && "bg-sky-50"
                )}
                onClick={() => { if (!selectMode) navigate(`/uadmin/files/${file.id}`); }}
              >
                {selectMode && (
                  <div className="col-span-1">
                    <input type="checkbox" className="h-4 w-4 accent-sky-600" checked={selectedFileIds.has(file.id)} onChange={(e) => { setSelectedFileIds(prev => { const next = new Set(prev); e.target.checked ? next.add(file.id) : next.delete(file.id); return next; }); }} onClick={(e) => e.stopPropagation()} />
                  </div>
                )}
                <div className={cn("col-span-6 flex items-center gap-3", selectMode && "col-span-5")}>
                  {(() => {
                    const FileIcon = getFileIconByExtension(file.name);
                    const iconColor = getFileIconColor(file.name);
                    return (
                      <div className={cn("p-2 rounded-lg flex-shrink-0", iconColor.bg)}>
                        <FileIcon size={20} className={iconColor.text} />
                      </div>
                    );
                  })()}
                  <span className="text-sm font-medium text-slate-700 truncate">{file.name}</span>
                </div>
                <div className="col-span-2 text-sm text-slate-500 capitalize">{file.type}</div>
                <div className="col-span-2 text-sm text-slate-500 font-mono">{formatFileSize(file.size)}</div>
                <div className="col-span-1 flex justify-end">
                  {!selectMode && (
                    <div className="relative">
                      <button 
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" 
                        onClick={(e) => { 
                          e.stopPropagation();
                          e.preventDefault();
                          console.log('[CategoriesPageNew] 点击了文件菜单按钮:', file.id);
                          const rect = e.currentTarget.getBoundingClientRect();
                          console.log('[CategoriesPageNew] 按钮位置:', rect);
                          if (perFileMenuOpen === file.id) {
                            // 已经打开，关闭它
                            setPerFileMenuOpen('');
                            setPerFileMenuRect(null);
                          } else {
                            // 打开菜单
                            setPerFileMenuRect(rect);
                            setPerFileMenuOpen(file.id);
                          }
                        }}
                        data-file-menu-trigger={file.id}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {perFileMenuOpen === file.id && perFileMenuRect && (
                        <MoreMenu
                          triggerRect={perFileMenuRect}
                          onPreview={() => { setPerFileMenuOpen(''); navigate(`/uadmin/files/${file.id}`); }}
                          onDownload={async () => {
                            setPerFileMenuOpen('');
                            console.log('下载文件:', file.name, 'storage_key:', file.storage_key);
                            if (!file.storage_key) {
                              const { showToast } = await import('../utils/toast');
                              showToast('文件不可下载');
                              return;
                            }
                            try {
                              const { getSignedUrl } = await import('../config/storage');
                              const { showToast } = await import('../utils/toast');
                              showToast('正在下载...');
                              const url = await getSignedUrl(file.storage_key);
                              console.log('获取到签名URL:', url);
                              // Fetch the file content
                              const response = await fetch(url);
                              const blob = await response.blob();
                              console.log('文件大小:', blob.size, 'bytes');
                              // Create object URL for the blob
                              const objectUrl = window.URL.createObjectURL(blob);
                              // Create temporary link and trigger download
                              const link = document.createElement('a');
                              link.href = objectUrl;
                              link.download = file.name;
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              link.click();
                              // Cleanup
                              setTimeout(() => {
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(objectUrl);
                              }, 100);
                            } catch (err) {
                              console.error('下载失败:', err);
                              const { showToast } = await import('../utils/toast');
                              showToast('下载失败，请重试');
                            }
                          }}
                          onCopyLink={async () => {
                            setPerFileMenuOpen('');
                            if (file.storage_key) {
                              try {
                                const { getPublicUrl } = await import('../config/storage');
                                const { showToast } = await import('../utils/toast');
                                const url = await getPublicUrl(file.storage_key);
                                await navigator.clipboard.writeText(url);
                                showToast('文件地址已复制');
                              } catch (err) {
                                console.error('复制失败:', err);
                                const { showToast } = await import('../utils/toast');
                                showToast('复制失败，请重试');
                              }
                            }
                          }}
                          onMove={() => { setPerFileMenuOpen(''); setPerFileMenuRect(null); setSelectedFileIds(new Set([file.id])); setMoveTarget(activeCategory?.id || ''); setMoveStep('select'); setShowMoveModal(true); }}
                          onDelete={() => { setPerFileMenuOpen(''); setPerFileMenuRect(null); setSelectedFileIds(new Set([file.id])); setShowDeleteModal(true); }}
                          onClose={() => { setPerFileMenuOpen(''); setPerFileMenuRect(null); }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* 空状态 */}
            {currentFiles.length === 0 && !isLoadingFiles && (
              <div className="py-12 text-center text-slate-400">
                <Folder size={48} className="mx-auto mb-3 opacity-50" />
                <p>此文件夹暂无文件</p>
              </div>
            )}
            
            {/* 首次加载状态 */}
            {isLoadingFiles && currentFiles.length === 0 && (
              <div className="py-12 flex flex-col items-center justify-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                <span className="text-sm">加载文件中...</span>
              </div>
            )}
            
            {/* 加载更多触发器（仅对大文件夹有效）*/}
            {hasMoreFiles && currentFiles.length > 0 && (
              <div 
                ref={loadMoreTriggerRef}
                className="py-6 flex items-center justify-center border-t border-slate-100"
              >
                {isLoadingFiles ? (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">加载更多...</span>
                  </div>
                ) : (
                  <button 
                    onClick={loadMoreFiles}
                    className="text-sm text-sky-500 hover:text-sky-600 px-4 py-2 rounded-lg hover:bg-sky-50 transition-colors"
                  >
                    加载更多 ({currentFiles.length} / {totalFilesCount})
                  </button>
                )}
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showUploadModal && <UploadModal onClose={() => setShowUploadModal(false)} category={activeCategory} />}
      {showCreateCategoryModal && activeCategory && <CreateCategoryModal parentCategory={activeCategory} onClose={() => setShowCreateCategoryModal(false)} />}

      {/* 删除确认模态框 */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">确认删除</h3>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {selectedFolderIds.size > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-800">已选择 {selectedFolderIds.size} 个文件夹，请使用"清空并删除"功能</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-600">将删除以下 {selectedFileIds.size} 个文件：</p>
                <ul className="mt-2 text-sm text-slate-700 list-disc pl-5 space-y-1 max-h-40 overflow-y-auto">
                  {(activeCategory?.files || []).filter((f: any) => selectedFileIds.has(f.id)).slice(0, 10).map((f: any) => (
                    <li key={f.id} className="truncate">{f.name}</li>
                  ))}
                </ul>
                {selectedFileIds.size > 10 && <div className="text-xs text-slate-400 mt-1">还有 {selectedFileIds.size - 10} 个...</div>}
              </div>
              {deleteProgress && (
                <div className="pt-2">
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-red-500 h-2 rounded-full transition-all" style={{ width: `${Math.round((deleteProgress.processed / deleteProgress.total) * 100)}%` }} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{deleteProgress.processed}/{deleteProgress.total}</div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => { if (!isDeleting) setShowDeleteModal(false); }} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50" disabled={isDeleting}>取消</button>
              <button
                onClick={async () => {
                  if (isDeleting || selectedFolderIds.size > 0) return;
                  setIsDeleting(true);
                  setDeleteProgress({ processed: 0, total: selectedFileIds.size });
                  await deleteFiles(Array.from(selectedFileIds), (p, _t) => setDeleteProgress({ processed: p, total: selectedFileIds.size }));
                  
                  // 🔄 刷新分类数据
                  await fetchCategories();
                  
                  setIsDeleting(false);
                  setShowDeleteModal(false);
                  setSelectedFileIds(new Set());
                  setDeleteProgress(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={isDeleting || selectedFolderIds.size > 0}
              >{isDeleting ? '删除中...' : '确认删除'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 移动文件模态框 */}
      {showMoveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">移动到</h3>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {moveStep === 'select' ? (
                <>
                  <p className="text-sm text-slate-600">选择目标文件夹</p>
                  <CategorySelector categories={categories as any[]} value={moveTarget} onChange={setMoveTarget} enableCreate excludeIds={new Set([...Array.from(selectedFolderIds)])} />
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-700">确认移动？</p>
                  <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 flex items-center">
                    <span className="font-medium">{getPath(activeCategory?.id)}</span>
                    <ArrowRight size={16} className="mx-2 text-slate-400" />
                    <span className="font-medium">{getPath(moveTarget)}</span>
                  </div>
                </>
              )}
              {moveProgress && (
                <div className="pt-2">
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-sky-500 h-2 rounded-full transition-all" style={{ width: `${Math.round((moveProgress.processed / moveProgress.total) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => { if (!isMoving) setShowMoveModal(false); }} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50" disabled={isMoving}>取消</button>
              {moveStep === 'select' ? (
                <button onClick={() => { if (moveTarget) setMoveStep('confirm'); }} className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50" disabled={!moveTarget}>下一步</button>
              ) : (
                <button
                  onClick={async () => {
                    if (!moveTarget) return;
                    try {
                      setIsMoving(true);
                      const total = selectedFileIds.size + selectedFolderIds.size;
                      setMoveProgress({ processed: 0, total });
                      if (selectedFileIds.size > 0) await useFileStore.getState().moveFilesToCategory(Array.from(selectedFileIds), moveTarget, (p) => setMoveProgress({ processed: p, total }));
                      if (selectedFolderIds.size > 0) {
                        let processed = selectedFileIds.size;
                        for (const cid of Array.from(selectedFolderIds)) {
                          await useCatStore.getState().moveCategory(cid, moveTarget);
                          processed++;
                          setMoveProgress({ processed, total });
                        }
                      }
                      setShowMoveModal(false);
                      setSelectedFileIds(new Set());
                      setSelectedFolderIds(new Set());
                      setMoveStep('select');
                    } catch (e: any) {
                      alert(e?.message || '移动失败');
                    } finally {
                      setIsMoving(false);
                      setMoveProgress(null);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50"
                  disabled={isMoving}
                >{isMoving ? '移动中...' : '确认移动'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 移动分类模态框 */}
      {showMoveCategoryModal && activeCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">移动文件夹</h3>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {moveCategoryStep === 'select' ? (
                <>
                  <p className="text-sm text-slate-600">选择新的父文件夹</p>
                  <CategorySelector categories={categories as any[]} value={moveCategoryTarget || ''} onChange={(id) => setMoveCategoryTarget(id)} excludeIds={new Set([activeCategory.id, ...Array.from(collectDescendantIds(activeCategory))])} enableCreate />
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-700">确认移动文件夹？</p>
                  <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700 flex items-center">
                    <span className="font-medium">{getPath(activeCategory?.parent_id)}</span>
                    <ArrowRight size={16} className="mx-2 text-slate-400" />
                    <span className="font-medium">{getPath(moveCategoryTarget)}</span>
                  </div>
                  <div className="text-xs text-slate-500">文件夹: {activeCategory.name}</div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => setShowMoveCategoryModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">取消</button>
              {moveCategoryStep === 'select' ? (
                <button onClick={() => { if (moveCategoryTarget) setMoveCategoryStep('confirm'); }} className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50" disabled={!moveCategoryTarget}>下一步</button>
              ) : (
                <button
                  onClick={async () => {
                    if (!moveCategoryTarget) return;
                    setIsCategoryMoving(true);
                    try {
                      await moveCategory(activeCategory.id, moveCategoryTarget);
                      setShowMoveCategoryModal(false);
                      setMoveCategoryStep('select');
                    } catch (e: any) {
                      alert(e?.message || '移动文件夹失败');
                    } finally {
                      setIsCategoryMoving(false);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50"
                  disabled={isCategoryMoving}
                >{isCategoryMoving ? '移动中...' : '确认移动'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 重命名模态框 */}
      {showRenameModal && activeCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">重命名文件夹</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">新名称</label>
                <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} className="w-full px-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent" placeholder="输入新名称" />
                {newCategoryName.trim() && siblingNamesForRename.includes(newCategoryName.trim().toLowerCase()) && newCategoryName.trim() !== activeCategory.name && (
                  <p className="mt-2 text-xs text-red-600">同级目录下已存在同名文件夹</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => setShowRenameModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50" disabled={isRenaming}>取消</button>
              <button
                onClick={async () => {
                  if (!newCategoryName.trim() || newCategoryName.trim() === activeCategory.name) { setShowRenameModal(false); return; }
                  if (siblingNamesForRename.includes(newCategoryName.trim().toLowerCase())) return;
                  setIsRenaming(true);
                  await updateCategory(activeCategory.id, { name: newCategoryName.trim() });
                  setIsRenaming(false);
                  setShowRenameModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 disabled:opacity-50"
                disabled={isRenaming || !newCategoryName.trim()}
              >{isRenaming ? '保存中...' : '保存'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 可见性设置模态框 */}
      {/* 对外关联确认弹窗 */}
      {showExternalLinkModal && activeCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">
                {activeCategory.is_external_linked ? '取消对外关联' : '设置对外关联'}
              </h3>
              <button onClick={() => setShowExternalLinkModal(false)} className="p-1 text-slate-400 rounded-full hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                {activeCategory.is_external_linked
                  ? '取消关联后，以下文件夹将在会员中心不可见：'
                  : '确认关联后，以下文件夹将在会员中心可见：'
                }
              </p>
              <div className="max-h-60 overflow-y-auto space-y-1 bg-slate-50 rounded-lg p-3 border border-slate-200">
                {externalLinkTargets.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 text-sm" style={{ paddingLeft: t.depth * 20 }}>
                    <Folder size={14} className={t.depth === 0 ? 'text-sky-500' : 'text-slate-400'} />
                    <span className={t.depth === 0 ? 'font-medium text-slate-800' : 'text-slate-600'}>{t.name}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-xs text-teal-700">
                  <strong>提示：</strong>共 {externalLinkTargets.length} 个文件夹将{activeCategory.is_external_linked ? '取消' : '开启'}对外关联。
                  关联后，会员中心管理面板将能浏览和导入这些文件夹中的文件。
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => setShowExternalLinkModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                取消
              </button>
              <button
                disabled={isUpdatingExternalLink}
                onClick={async () => {
                  setIsUpdatingExternalLink(true);
                  try {
                    const newValue = !activeCategory.is_external_linked;
                    const ids = externalLinkTargets.map(t => t.id);
                    // 批量更新所有文件夹的 is_external_linked
                    const { error } = await (await import('../config/supabase')).supabase
                      .from('categories')
                      .update({ is_external_linked: newValue })
                      .in('id', ids);
                    if (error) throw error;
                    await fetchCategories();
                    setShowExternalLinkModal(false);
                  } catch (e: any) {
                    alert(e?.message || '操作失败');
                  } finally {
                    setIsUpdatingExternalLink(false);
                  }
                }}
                className={cn(
                  'px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50',
                  activeCategory.is_external_linked
                    ? 'bg-slate-500 hover:bg-slate-600'
                    : 'bg-teal-500 hover:bg-teal-600'
                )}
              >
                {isUpdatingExternalLink ? '处理中...' : (activeCategory.is_external_linked ? '确认取消关联' : '确认关联')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 可见性设置模态框 */}
      {showVisibilityModal && activeCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">设置文件夹可见性</h3>
              <button onClick={() => setShowVisibilityModal(false)} className="p-1 text-slate-400 rounded-full hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-3">
              {[
                { value: 'private', label: '私有', desc: '只有创建者和被授权用户可见', icon: EyeOff, color: 'text-slate-500' },
                { value: 'public', label: '完全公开', desc: '所有用户都可以看到和访问', icon: Eye, color: 'text-green-500' },
                { value: 'restricted_public', label: '受限公开', desc: '默认隐藏，通过订单查询后可访问', icon: Shield, color: 'text-orange-500' },
              ].map((opt) => (
                <label key={opt.value} className={cn("flex items-center p-3 border rounded-lg cursor-pointer transition-colors", selectedVisibilityType === opt.value ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:bg-slate-50')}>
                  <input type="radio" name="visibility" value={opt.value} checked={selectedVisibilityType === opt.value} onChange={(e) => setSelectedVisibilityType(e.target.value as VisibilityType)} className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-slate-300" />
                  <opt.icon className={cn("w-5 h-5 ml-3 mr-2", opt.color)} />
                  <div>
                    <div className="text-sm font-medium text-slate-700">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => setShowVisibilityModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">取消</button>
              <button
                onClick={async () => {
                  await updateCategory(activeCategory.id, { visibility_type: selectedVisibilityType, is_public: selectedVisibilityType === 'public' });
                  setShowVisibilityModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600"
              >保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 租户设置模态框 */}
      {showTenantModal && activeCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">设置文件夹区域</h3>
              <button onClick={() => setShowTenantModal(false)} className="p-1 text-slate-400 rounded-full hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-3">
              {[
                { value: 'cn', label: '国内 (CN)', desc: '中国大陆地区用户访问', icon: MapPin, color: 'text-red-500', bgSelected: 'border-red-500 bg-red-50' },
                { value: 'intl', label: '国际 (INTL)', desc: '海外及港澳台地区用户访问', icon: Globe, color: 'text-blue-500', bgSelected: 'border-blue-500 bg-blue-50' },
              ].map((opt) => (
                <label key={opt.value} className={cn("flex items-center p-3 border rounded-lg cursor-pointer transition-colors", selectedTenant === opt.value ? opt.bgSelected : 'border-slate-200 hover:bg-slate-50')}>
                  <input type="radio" name="tenant" value={opt.value} checked={selectedTenant === opt.value} onChange={(e) => setSelectedTenant(e.target.value as 'cn' | 'intl')} className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-slate-300" />
                  <opt.icon className={cn("w-5 h-5 ml-3 mr-2", opt.color)} />
                  <div>
                    <div className="text-sm font-medium text-slate-700">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-sm text-amber-800">
                  <strong>⚠️ 注意：</strong>修改文件夹区域后，该文件夹将只对对应区域的用户可见。请确认此操作。
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => setShowTenantModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">取消</button>
              <button
                onClick={async () => {
                  await updateCategory(activeCategory.id, { tenant: selectedTenant });
                  setShowTenantModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600"
              >保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 清空并删除分类模态框 */}
      {showClearModal && (clearTargetCategory || activeCategory) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">清空并删除文件夹</h3>
            </div>
            <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
              {clearStep === 'confirm-files' && clearTreeInfo && (
                <>
                  <p className="text-sm text-slate-700">将删除文件夹 <strong>{(clearTargetCategory || activeCategory).name}</strong> 中的 {clearTreeInfo.fileIds.length} 个文件</p>
                  <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1 max-h-40 overflow-y-auto">
                    {clearTreeInfo.fileNames.slice(0, 20).map((n, idx) => <li key={idx} className="truncate">{n}</li>)}
                  </ul>
                  {clearTreeInfo.fileIds.length > 20 && <div className="text-xs text-slate-400">还有 {clearTreeInfo.fileIds.length - 20} 个...</div>}
                </>
              )}
              {clearStep === 'deleting-files' && (
                <>
                  <p className="text-sm text-slate-700">正在删除文件...</p>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="bg-red-500 h-2 rounded-full transition-all" style={{ width: `${clearProgress ? Math.round((clearProgress.processed / clearProgress.total) * 100) : 0}%` }} />
                  </div>
                  {clearProgress && <div className="text-xs text-slate-500">{clearProgress.processed}/{clearProgress.total}</div>}
                </>
              )}
              {clearStep === 'confirm-category' && (
                <p className="text-sm text-slate-700">文件已删除，确认删除文件夹 <strong>{(clearTargetCategory || activeCategory).name}</strong>？</p>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-xl">
              <button onClick={() => { if (clearStep !== 'deleting-files') setShowClearModal(false); }} disabled={clearStep === 'deleting-files'} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">取消</button>
              {clearStep === 'confirm-files' && (
                <button
                  onClick={async () => {
                    const info = clearTreeInfo || collectTreeInfo(clearTargetCategory || activeCategory);
                    setClearStep('deleting-files');
                    setClearProgress({ processed: 0, total: info.fileIds.length });
                    if (info.fileIds.length > 0) await deleteFiles(info.fileIds, (p, t) => setClearProgress({ processed: p, total: t }));
                    
                    // 🔄 刷新分类数据
                    await fetchCategories();
                    
                    setClearStep('confirm-category');
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >删除文件</button>
              )}
              {clearStep === 'confirm-category' && (
                <button
                  onClick={async () => {
                    const info = clearTreeInfo || collectTreeInfo(clearTargetCategory || activeCategory);
                    for (let i = info.categoryIds.length - 1; i >= 0; i--) {
                      await useCategoryStore.getState().deleteCategory(info.categoryIds[i]);
                    }
                    setShowClearModal(false);
                    setClearProgress(null);
                    setSelectedFolderIds(prev => { const next = new Set(prev); info.categoryIds.forEach(id => next.delete(id)); return next; });
                    setClearTargetCategory(null);
                    setClearTreeInfo(null);
                    setClearStep('confirm-files');
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >删除文件夹</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PDF 缩略图生成进度浮层 */}
      {thumbnailGenProgress && (
        <div className="fixed bottom-6 right-6 z-50 bg-white rounded-xl shadow-2xl border border-slate-200 px-5 py-4 w-80">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 size={18} className="animate-spin text-teal-500" />
            <span className="text-sm font-medium text-slate-700">{thumbnailGenProgress.phase}</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mb-1.5">
            <div
              className="bg-teal-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.round((thumbnailGenProgress.processed / thumbnailGenProgress.total) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-slate-400 text-right">{thumbnailGenProgress.processed} / {thumbnailGenProgress.total}</p>
        </div>
      )}

      {/* 批量下载选择弹窗 */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">下载方式</h3>
              <button 
                onClick={() => { if (!isDownloading) setShowDownloadModal(false); }} 
                disabled={isDownloading}
                className="p-1 text-slate-400 rounded-full hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {!isDownloading ? (
                <>
                  <p className="text-sm text-slate-600 mb-4">
                    已选择 <span className="font-semibold text-sky-600">{selectedFileIds.size}</span> 个文件
                    {selectedFileIds.size > 5 && (
                      <span className="text-orange-500 ml-2">（建议打包下载）</span>
                    )}
                  </p>
                  
                  <div className="space-y-3">
                    {/* 打包下载选项 */}
                    <button
                      onClick={async () => {
                        setIsDownloading(true);
                        const { showToast } = await import('../utils/toast');
                        const { getSignedUrl } = await import('../config/storage');
                        const JSZip = (await import('jszip')).default;
                        
                        const files = currentFiles.filter((f: any) => selectedFileIds.has(f.id));
                        const zip = new JSZip();
                        
                        setDownloadProgress({ current: 0, total: files.length });
                        
                        for (let i = 0; i < files.length; i++) {
                          const file = files[i];
                          if (file.storage_key) {
                            try {
                              setDownloadProgress({ current: i + 1, total: files.length, fileName: file.name });
                              const url = await getSignedUrl(file.storage_key);
                              const response = await fetch(url);
                              const blob = await response.blob();
                              zip.file(file.name, blob);
                            } catch (err) {
                              console.error('获取文件失败:', file.name, err);
                            }
                          }
                        }
                        
                        // 生成 ZIP 文件名：文件夹名_文件数量_日期
                        const folderName = activeCategory?.name || '文件';
                        const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                        const zipFileName = `${folderName}_${files.length}个文件_${dateStr}.zip`;
                        
                        setDownloadProgress({ current: files.length, total: files.length, fileName: '正在生成压缩包...' });
                        
                        const zipBlob = await zip.generateAsync({ type: 'blob' });
                        const objectUrl = window.URL.createObjectURL(zipBlob);
                        const link = document.createElement('a');
                        link.href = objectUrl;
                        link.download = zipFileName;
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        
                        setTimeout(() => {
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(objectUrl);
                        }, 100);
                        
                        setIsDownloading(false);
                        setDownloadProgress(null);
                        setShowDownloadModal(false);
                        showToast('下载完成');
                      }}
                      className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-sky-300 hover:bg-sky-50/50 transition-colors text-left"
                    >
                      <div className="p-3 bg-sky-100 rounded-xl text-sky-600">
                        <Archive size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">打包下载</div>
                        <div className="text-sm text-slate-500">将所有文件打包为 ZIP 压缩包</div>
                      </div>
                      {selectedFileIds.size > 5 && (
                        <span className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-100 rounded-full">推荐</span>
                      )}
                    </button>
                    
                    {/* 逐个下载选项 */}
                    <button
                      onClick={async () => {
                        setIsDownloading(true);
                        const { showToast } = await import('../utils/toast');
                        const { getSignedUrl } = await import('../config/storage');
                        
                        const files = currentFiles.filter((f: any) => selectedFileIds.has(f.id));
                        setDownloadProgress({ current: 0, total: files.length });
                        
                        for (let i = 0; i < files.length; i++) {
                          const file = files[i];
                          if (file.storage_key) {
                            try {
                              setDownloadProgress({ current: i + 1, total: files.length, fileName: file.name });
                              const url = await getSignedUrl(file.storage_key);
                              const response = await fetch(url);
                              const blob = await response.blob();
                              const objectUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = objectUrl;
                              link.download = file.name;
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              link.click();
                              setTimeout(() => {
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(objectUrl);
                              }, 100);
                              // 延迟避免浏览器阻止多个下载
                              await new Promise(r => setTimeout(r, 800));
                            } catch (err) {
                              console.error('下载失败:', file.name, err);
                            }
                          }
                        }
                        
                        setIsDownloading(false);
                        setDownloadProgress(null);
                        setShowDownloadModal(false);
                        showToast('下载完成');
                      }}
                      className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-sky-300 hover:bg-sky-50/50 transition-colors text-left"
                    >
                      <div className="p-3 bg-slate-100 rounded-xl text-slate-600">
                        <FileDown size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">逐个下载</div>
                        <div className="text-sm text-slate-500">分别下载每个文件（{selectedFileIds.size} 个）</div>
                      </div>
                    </button>
                  </div>
                </>
              ) : (
                <div className="py-4">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-10 h-10 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin"></div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-700">
                      正在下载 {downloadProgress?.current}/{downloadProgress?.total}
                    </p>
                    {downloadProgress?.fileName && (
                      <p className="text-xs text-slate-500 mt-1 truncate max-w-xs mx-auto">
                        {downloadProgress.fileName}
                      </p>
                    )}
                  </div>
                  <div className="mt-4 w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-sky-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${downloadProgress ? (downloadProgress.current / downloadProgress.total) * 100 : 0}%` }} 
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 检查重复文件模态框 */}
      {activeCategory && (
        <DuplicateFilesModal
          isOpen={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          categoryId={activeCategory.id}
          categoryName={activeCategory.name}
          onDeleted={() => {
            // 刷新当前分类的文件列表
            fetchCategoryFiles(activeCategory.id, { forceRefresh: true });
          }}
        />
      )}

      {/* 通用确认弹窗 */}
      <ConfirmDialog
        open={pageConfirm.open}
        title={pageConfirm.title}
        message={pageConfirm.message}
        variant={pageConfirm.variant}
        confirmText={pageConfirm.confirmText}
        onConfirm={pageConfirm.onConfirm}
        onCancel={() => setPageConfirm(p => ({ ...p, open: false }))}
      />
    </div>
  );
};

export default CategoriesPageNew;
